// ── SYNC MERGE + SOFT DELETE ──────────────────────────────────────────────
// Deletion safety for the whole-collection sync model.
//
// The app syncs each collection as a whole JSON array. Physically removing a
// record on one device does not stop another device (which still holds the
// record) from pushing it back — the deletion is silently undone. The fix is
// SOFT DELETE + TOMBSTONES: a delete marks the record `deleted:true` with a
// `deletedAt` timestamp instead of removing it. The tombstone travels with the
// array during sync, and this merge guarantees a deletion beats an older,
// still-live copy. Every calculation reads only ACTIVE (non-deleted) records.
//
// Pure: no React, no storage. Locked down by syncMerge.test.js.

export const isDeleted = (r) => !!(r && r.deleted);

// Active (non-deleted) records — the ONLY thing calculations/reports should see.
export const activeRecords = (arr) =>
  Array.isArray(arr) ? arr.filter((r) => !isDeleted(r)) : [];

// Comparable timestamp (ms) for conflict resolution: the newest of a record's
// updatedAt / deletedAt. Missing/invalid dates sort oldest (0).
const ts = (v) => {
  const t = v ? Date.parse(v) : NaN;
  return Number.isFinite(t) ? t : 0;
};
export const recordStamp = (r) => Math.max(ts(r && r.updatedAt), ts(r && r.deletedAt));

// Merge a cloud array and a local array by `id`.
//   • For each id, keep the version with the NEWEST stamp.
//   • On a tie, a DELETED tombstone wins (deletion is durable).
//   • Records without an id can't be de-duplicated, so both sides are kept.
// Returns the FULL merged array INCLUDING tombstones, so the result can be
// persisted / re-synced and the deletions are never lost.
export const mergeById = (cloud = [], local = []) => {
  const byId = new Map();
  // Process cloud FIRST, then local, so that on a tie the LOCAL copy (processed
  // last) wins — this preserves each device's un-synced edits instead of letting
  // a stale cloud copy stomp them. Deletions still win regardless of order,
  // because a tombstone carries a deletedAt timestamp that outranks an untimed
  // live copy, and the tie rule never lets a live record overwrite a tombstone.
  const put = (r) => {
    if (!r || r.id == null) return;
    const prev = byId.get(r.id);
    if (!prev) { byId.set(r.id, r); return; }
    const a = recordStamp(prev);
    const b = recordStamp(r);
    if (b > a) { byId.set(r.id, r); return; }          // strictly newer wins
    if (b < a) return;                                  // strictly older loses
    // Equal stamp (typically two untimed copies, or same edit time):
    // replace UNLESS that would overwrite a tombstone with a live record.
    if (!(isDeleted(prev) && !isDeleted(r))) byId.set(r.id, r);
  };
  (cloud || []).forEach(put);
  (local || []).forEach(put);
  const noId = [
    ...(cloud || []).filter((r) => r && r.id == null),
    ...(local || []).filter((r) => r && r.id == null),
  ];
  return [...byId.values(), ...noId];
};

// Detect SAME-RECORD concurrent edits: an id on both sides where both carry a
// real timestamp and they differ. mergeById resolves these by newest-wins; this
// surfaces them (for a dev conflict log) so a resolution is never silent.
export const detectConflicts = (cloud = [], local = []) => {
  const cloudById = new Map((cloud || []).filter((r) => r && r.id != null).map((r) => [String(r.id), r]));
  const out = [];
  (local || []).forEach((l) => {
    if (!l || l.id == null) return;
    const c = cloudById.get(String(l.id));
    if (!c) return;
    const ct = recordStamp(c);
    const lt = recordStamp(l);
    if (ct > 0 && lt > 0 && ct !== lt) {
      out.push({ id: l.id, cloudStamp: ct, localStamp: lt, winner: lt > ct ? 'local' : 'cloud', reason: 'newer_updatedAt', deletionInvolved: isDeleted(c) || isDeleted(l) });
    }
  });
  return out;
};

// Return a NEW record marked as a tombstone. `updatedAt` is bumped too so the
// deletion also wins any updatedAt-based comparison.
export const tombstone = (record, meta = {}) => {
  const when = meta.deletedAt || new Date().toISOString();
  const out = { ...record, deleted: true, deletedAt: when, updatedAt: when };
  if (meta.deletedBy) out.deletedBy = meta.deletedBy;
  return out;
};

// Mark the record with the given id as deleted, in place within an array
// (returns a new array). Non-matching records are returned unchanged.
export const softDeleteById = (arr, id, meta = {}) =>
  (arr || []).map((r) => (r && (r.id === id || String(r.id) === String(id)) ? tombstone(r, meta) : r));

// ARCHIVE (not delete): for master records (products, customers, suppliers)
// that are referenced by historical transactions. Archived records STAY in the
// active arrays so history/reporting/WAC still resolve them; UI dropdowns for
// NEW entries filter out `archived`. Distinct from a tombstone, which removes
// the record from everything.
export const archiveById = (arr, id, meta = {}) => {
  const when = meta.at || new Date().toISOString();
  return (arr || []).map((r) =>
    (r && (r.id === id || String(r.id) === String(id))
      ? { ...r, archived: true, archivedAt: when, updatedAt: when }
      : r));
};
export const isArchived = (r) => !!(r && r.archived);
export const notArchived = (arr) => (Array.isArray(arr) ? arr.filter((r) => !isArchived(r)) : []);
